import React, { useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../store';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { getCreditCardDots } from '../../../utils/credit-card-mask';
import SelectInput from '../../common/inputs/select-input';
import StringInput from '../../common/inputs/string-input';
import useRegisterErrorField from '../../common/hooks/register-error-field-hook';

const PaymentInformation = observer(() => {
  const { t } = useTranslation('', { keyPrefix: 'PAYMENT_INFORMATION' });
  const { t: tError } = useTranslation('', { keyPrefix: 'PAYMENT_INFORMATION.ERRORS' });
  const { paymentInformationStore: store, signUpStore, tenantStore } = useStore();
  const { information } = store;
  const fieldRef = useRef<HTMLDivElement | null>(null);
  useRegisterErrorField(fieldRef, information.creditCardNumber.errorType);
  const handleChange = (field: keyof typeof information, value: string) => {
    store.updateInformation(field, value);
  };
  const [editCardNumber, setEditCardNumber] = React.useState<boolean>(false);
  const paymentOptions = useMemo(
    () => tenantStore.config.paymentOptions.split(',').map(x => x.trim()),
    [tenantStore.config.paymentOptions],
  );

  const updateCreditCardNumber = (value: string) => {
    handleChange('creditCardNumber', value);
  };

  const setCreditCardResult = (result: any) => {
    if (result?.token && result.token.length > 0) {
      handleChange('creditCardToken', result.token);
      store.setError('creditCardNumber', undefined);
      setEditCardNumber(true);
    } else {
      handleChange('creditCardToken', '');
      store.setError('creditCardNumber', 'invalid');
    }
  };

  const validateCreditCardNumber = () => {
    if (!information.creditCardNumber.value) return;
    let cctype = '';
    switch (information.creditCardType.value) {
      case 'VISA':
        cctype = 'VI';
        break;
      case 'AMEX':
        cctype = 'AX';
        break;
      case 'MASTER':
        cctype = 'MC';
        break;
    }
    const result = window.cctoken.validate(information.creditCardNumber.value, cctype);
    if (result !== 'valid') {
      store.setError('creditCardNumber', 'invalid');
    } else {
      window.cctoken.getToken(information.creditCardNumber.value, setCreditCardResult);
    }
  };

  const tokenizeAccountCode = () => {
    if (!information.debitCardAccountCode.value) return;
    window.cctoken.getToken(information.debitCardAccountCode.value, (result: any) => {
      handleChange('creditCardToken', '');
      if (result?.token && result.token.length > 0) {
        handleChange('creditCardToken', result.token);
      }
    });
  };

  return (
    <div className={'step'}>
      <h2>{t('TITLE')}</h2>
      <span className={'step-info'}>{t('INFO')}</span>
      <div className="container-contact">
        <SelectInput
          value={information.paymentOption}
          label={t('PAYMENT_OPTIONS')}
          error={information.paymentOption.errorType ? tError(`${information.paymentOption.errorType.toUpperCase()}_PAYMENT_OPTION`) : ''}
          selectLabel={t('SELECT')}
          onUpdate={value => handleChange('paymentOption', value)}
          selectValues={paymentOptions.map(option => ({ label: t(option.replaceAll(' ', '_').toUpperCase()), value: option }))}
        />

        {information.paymentOption.value === 'Credit Card' && (
          <div>
            <SelectInput
              value={information.creditCardType}
              label={t('CARD_TYPE')}
              error={information.creditCardType.errorType ? tError(`${information.creditCardType.errorType.toUpperCase()}_CARD_TYPE`) : ''}
              selectLabel={t('SELECT')}
              onUpdate={value => handleChange('creditCardType', value)}
              selectValues={[
                { label: 'Master Card', value: 'MASTER' },
                { label: 'Visa', value: 'VISA' },
                { label: 'American Express', value: 'AMEX' },
              ]}
            />

            <div className={'container-input'} ref={fieldRef}>
              <label>{t('CREDIT_CARD_NUMBER')}</label>
              <div className={'controls'}>
                {!editCardNumber && information.creditCardToken.value ? (
                  <div>
                    <button type="button" className="n2pc-btn-text edit-btn" onClick={() => setEditCardNumber(true)}>
                      {t('EDIT')}
                    </button>
                    <div className="d-flex container-card-number">
                      {getCreditCardDots(information.creditCardType.value)}
                      <span className={'card-dot-group'}>{information.creditCardNumber.value.slice(-4)}</span>
                    </div>
                  </div>
                ) : (
                  <input
                    type="text"
                    className={classNames(information.creditCardNumber.errorType && signUpStore.showErrors && 'error')}
                    value={information.creditCardNumber.value}
                    disabled={!information.creditCardType.value}
                    onChange={e => updateCreditCardNumber(e.target.value)}
                    onBlur={() => validateCreditCardNumber()}
                  />
                )}

                {information.creditCardNumber.errorType && signUpStore.showErrors && (
                  <label className={'error'}>{tError(`${information.creditCardNumber.errorType.toUpperCase()}_CREDIT_CARD_NUMBER`)}</label>
                )}
              </div>
            </div>

            <StringInput
              value={information.creditCardHolderName}
              label={t('CARD_HOLDER_NAME')}
              name={'creditCardHolderName'}
              onUpdate={value => handleChange('creditCardHolderName', value)}
              error={
                information.creditCardHolderName.errorType
                  ? tError(`${information.creditCardHolderName.errorType.toUpperCase()}_CREDIT_CARD_HOLDER_NAME`)
                  : ''
              }
              disabled={!information.creditCardType.value}
            />
          </div>
        )}

        {information.paymentOption.value === 'Debit' && (
          <div className={'container-two-columns'}>
            <StringInput
              value={information.debitCardBankName}
              name={'debitCardBankName'}
              label={t('BANK_NAME')}
              onUpdate={value => handleChange('debitCardBankName', value)}
              error={
                information.debitCardBankName.errorType
                  ? tError(`${information.debitCardBankName.errorType.toUpperCase()}_DEBIT_CARD_BANK_NAME`)
                  : ''
              }
            />
            <StringInput
              value={information.debitCardAccountCode}
              label={t('ACCOUNT_CODE')}
              name={'debitCardAccountCode'}
              onUpdate={value => handleChange('debitCardAccountCode', value)}
              onBlur={() => tokenizeAccountCode()}
              error={
                information.debitCardAccountCode.errorType
                  ? tError(`${information.debitCardAccountCode.errorType.toUpperCase()}_DEBIT_CARD_ACCOUNT_CODE`)
                  : ''
              }
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default PaymentInformation;
